import { Link } from "react-router-dom";
import {
  Scale, Briefcase, Landmark, RotateCcw, GraduationCap, ArrowRight, LifeBuoy,
} from "lucide-react";
import { FIRM } from "@/data/content";
import { SERVICE_GROUPS } from "@/data/servicesIA";
import { PageHero, Breadcrumbs, TestimonialBand, FadeIn } from "@/components/Shared";

const ICONS = { Scale, Briefcase, Landmark, RotateCcw, GraduationCap };

export default function Services() {
  return (
    <main data-testid="services-page">
      <PageHero
        eyebrow="Route 1 — Our Services"
        title="Criminal Solicitors London"
        lead="Browse our services by offence. Our team of expert criminal defence solicitors are focused on achieving the best possible result for each client — rigorously pursuing all avenues, keeping you involved and updated every step of the way."
      />
      <Breadcrumbs items={[{ label: "Our Services" }]} />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <FadeIn>
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className="max-w-3xl text-lg font-light leading-relaxed text-slate-600">
                  We know that, for many, being accused of a crime can be a daunting and
                  frightening experience.{" "}
                  <span className="font-medium text-brand-navy">Let us take it from here.</span>
                </p>
                <Link
                  to="/how-we-help"
                  className="brand-text-link mt-6 flex items-center gap-2"
                  data-testid="services-to-how-we-help"
                >
                  <LifeBuoy className="size-4" /> Not sure where to start? Browse by situation
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>
              <div className="aspect-[4/3] overflow-hidden rounded-sm border border-slate-200 md:rounded lg:col-span-5">
                <img
                  src={FIRM.legalContextImage}
                  alt="Bronze scales of justice"
                  className="size-full object-cover"
                  data-testid="services-legal-context-image"
                />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="brand-panel-pale py-20 sm:py-28 lg:py-32">
        <div className="brand-container space-y-8">
          {SERVICE_GROUPS.map((group, gi) => {
            const Icon = ICONS[group.icon] || Scale;
            return (
              <FadeIn key={group.slug} delay={gi * 0.05}>
                <div className="rounded-sm border border-slate-200 bg-white p-12 md:rounded" data-testid={`service-group-${group.slug}`}>
                  <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-start">
                    <div className="max-w-xl">
                      <Icon className="size-7 text-brand-gold" />
                      <h2 className="brand-heading mt-5 text-2xl sm:text-3xl">
                        {group.title}
                      </h2>
                      <p className="mt-3 text-sm leading-relaxed text-slate-500">{group.tagline}</p>
                      <Link
                        to={`/services/${group.slug}`}
                        className="brand-text-link mt-5 inline-flex items-center gap-2"
                        data-testid={`group-link-${group.slug}`}
                      >
                        {group.children.length > 0 ? `Explore ${group.title}` : "Read more"}
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </div>
                    {group.children.length > 0 && (
                      <ul className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:max-w-lg">
                        {group.children.map((child) => (
                          <li key={child.slug}>
                            <Link
                              to={`/services/${group.slug}/${child.slug}`}
                              className="group flex items-center justify-between border border-slate-200 bg-brand-ivory px-4 py-3 text-sm text-slate-700 transition-all duration-200 hover:border-brand-gold/60 hover:bg-white hover:text-brand-navy"
                              data-testid={`offence-link-${child.slug}`}
                            >
                              {child.title}
                              <ArrowRight className="size-3.5 shrink-0 text-brand-gold opacity-0 transition-opacity group-hover:opacity-100" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </section>

      <TestimonialBand />
    </main>
  );
}
